/* eslint-disable prettier/prettier */
import { IncomingHttpHeaders } from 'http';
import router from 'next/router';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { APP_ROUTES } from '@constants/constants';
import { AppDispatch } from '@/configureStore';
import urlsService from '@/services/urls-service';
import { transformGetPaginatedProductsListResponse } from './transformGetPaginatedProductsListResponse';
import * as Types from './types';

interface ProductsThunkRequest {
  apiUrl: string;
  headers?: IncomingHttpHeaders;
}

export const fetchProductsThunk = createAsyncThunk<Types.MappedResponse | undefined, ProductsThunkRequest>(
  'productsListing/fetch',
  async ({ apiUrl, headers }, { rejectWithValue }) => {
    try {
      const cookieHeader = headers?.cookie || '';

      const response = await fetch(apiUrl, {
        headers: {
          cookie: cookieHeader
        }
      });
      const data = await response.json();

      const mappedResponseData = transformGetPaginatedProductsListResponse(data);

      return mappedResponseData;
    } catch (e) {
      rejectWithValue(e);
    }
  }
);

export const productsListingSlice = createSlice({
  name: 'productsListing',
  initialState: {
    entities: {},
    loading: Types.LoadingState.IDLE,
    facetsOpen: false
  },
  reducers: {
    toggleFacetsOpen: (state) => {
      state.facetsOpen = !state.facetsOpen;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProductsThunk.fulfilled, (state, action) => {
      return {
        ...state,
        loading: Types.LoadingState.FULFILLED,
        entities: {
          ...action.payload
        }
      };
    });

    builder.addCase(fetchProductsThunk.pending, (state) => {
      state.loading = Types.LoadingState.PENDING;
    });

    builder.addCase(fetchProductsThunk.rejected, (state) => {
      state.loading = Types.LoadingState.REJECTED;
    });
  }
});

export const { toggleFacetsOpen } = productsListingSlice.actions;

interface DispatchGetProducts {
  apiUrl: string;
  webUrl?: string;
  queryParams?: any;
  facetsPath?: string;
  sortBy?: string;
}

export function getProducts(dispatch: AppDispatch, headers?: IncomingHttpHeaders) {
  return async function ({ apiUrl, webUrl, queryParams, facetsPath, sortBy }: DispatchGetProducts) {

    const appendPathname = [
      ...(sortBy ? ['sort', `${sortBy}`] : []),
      ...(facetsPath ? [facetsPath] : [])
    ].filter(Boolean).join('/');

    const urlOptions = {
      ...(appendPathname ? { appendPathname } : {}),
      queryParams: {
        wp: APP_ROUTES.CATALOG,
        ...(queryParams && { ...queryParams })
      }
    };

    const requestUrl = urlsService.getUrl(apiUrl, urlOptions);
    if (webUrl && typeof document !== 'undefined') {
      const browserUrl = new URL(`${document.location.origin}${webUrl}`);
      webUrl && router.push(browserUrl.toString());
    }

    await dispatch(fetchProductsThunk({ apiUrl: requestUrl.toString(), headers }));
  };
}

interface DispatchGetProductsByUrl {
  path: string;
  sort?: Types.SortOptions;
  queryParams?: Record<string, string | number | boolean>;
}
export function getProductsByUrl(dispatch: AppDispatch, headers?: IncomingHttpHeaders) {
  return async function ({ path, sort, queryParams }: DispatchGetProductsByUrl) {
    const urlOptions = {
      appendPathname: sort ? `${sort}/${path}` : path,
      queryParams: {
        wp: APP_ROUTES.CATALOG,
        ...(queryParams && { ...queryParams })
      }
    };
    const requestUrl = urlsService.getUrl('/products/searchByUrl/c', urlOptions);
    await dispatch(fetchProductsThunk({ apiUrl: requestUrl, headers }));
  };
}
